import React, { useEffect, useState, useRef, useMemo } from 'react';
import FormBuilderComponent from './form_builder';
import axiosInstance from '../../../axiosInstance';
import { t } from 'i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Formik, Form, Field } from 'formik';
import TextInput from '../../common/Form/textInput';
import Grid from 'apollo-react/components/Grid';
import CommonModal from '../../common/CommonModal';
import useUnsavedChangesWarning from '../../../hooks/ui/navigation/useUnsavedChangesWarning';
import { updateSubformSchema } from '../../../validation/subform';

export default function SubFormsFormBuilder({ persona }) {
  const { id } = useParams();
  const [form, setform] = useState();
  const [formCopy, setformCopy] = useState();
  const [discardModal, setDiscardModal] = useState(false);
  const [builderDirty, setBuilderDirty] = useState(false);
  const formBuilderSaveRef = useRef(null);
  const [formDataFields, setFormDataFields] = useState({
    isTemplate: false,
    formFields: [],
  });

  const fieldsDirty = useMemo(() => {
    if (!form || !formCopy) return false;
    return (
      form.name !== formCopy.name || form.description !== formCopy.description
    );
  }, [form, formCopy]);

  const isDirty = fieldsDirty || builderDirty;

  const { navigateBack } = useUnsavedChangesWarning(isDirty);
  const getSubForm = async () => {
    await axiosInstance
      // eslint-disable-next-line no-undef
      .get(`${process.env.REACT_APP_SERVER_URL}/api/subforms/${id}`, {
        withCredentials: true,
      })
      .then((response) => {
        console.log(response.data);

        setform({
          ...response.data,
          description: response.data.description
            ? response.data.description
            : '',
        });
        setformCopy({
          ...response.data,
          description: response.data.description
            ? response.data.description
            : '',
        });
        setFormDataFields({
          ...formDataFields,
          formFields: response.data['json_object'],
        });
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    if (discardModal) {
      setFormDataFields({ ...formDataFields, isTemplate: false });
    }
  }, [discardModal]);

  const handleFormSave = async (formJsonData) => {
    const validationResult = updateSubformSchema(t).safeParse({
      name: form.name,
      description: form.description,
      jsonObject: formJsonData,
    });
    if (!validationResult.success) {
      const messages = validationResult.error.issues.map((d) => d.message);
      toast.error(messages.join('\n'), { position: 'top-center' });
      return false;
    }
    try {
      await axiosInstance.put(
        // eslint-disable-next-line no-undef
        `${process.env.REACT_APP_SERVER_URL}/api/subforms/${id}`,
        { ...form, json_object: formJsonData },
        { withCredentials: true }
      );

      toast.success(t('Form Updated Successfully!'), {
        position: 'top-center',
      });
      setformCopy({ ...form });
      navigateBack();
    } catch (error) {
      console.error(t('Error saving form'), ':', error);
      const details = error?.response?.data?.error?.details;
      if (details) {
        const messages = Array.isArray(details)
          ? details.map((d) => d.message || 'Validation error')
          : Object.values(details);
        toast.error(messages.join('\n'), { position: 'top-center' });
      } else {
        const message =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          t('Failed to save form.');
        toast.error(message, { position: 'top-center' });
      }
      return false;
    }
  };
  const handleFormChange = (name, value) => {
    setform({
      ...form,
      [name]: value,
    });
  };

  useEffect(() => {
    getSubForm();
  }, []);

  const renderBasicFormInfo = () => {
    const basicFormInfo = (
      <div>
        <Field name="name">
          {({ field, meta }) => (
            <TextInput
              {...field}
              meta={meta}
              label={'Subform name'}
              value={form?.[`name`] || ''}
              onChange={(value) => handleFormChange('name', value)}
              required
            />
          )}
        </Field>

        <Field name="description">
          {({ field, meta }) => (
            <TextInput
              {...field}
              meta={meta}
              label={'Subform Description'}
              value={form?.[`description`]}
              onChange={(value) => handleFormChange('description', value)}
              multiline={true}
              textareaWidth={'100%'}
            />
          )}
        </Field>
      </div>
    );
    return basicFormInfo;
  };

  return (
    form && (
      <div>
        <div
          className="content"
          style={{
            border: 'solid 1px #E9E9E9',
            padding: '24px',
            borderRadius: '5px',
          }}
        >
          <Grid container spacing={2} sx={{ width: '100%' }}>
            <Grid size={12}>
              <Formik
                initialValues={form}
                enableReinitialize={true}
                onSubmit={() => {}}
              >
                <Form>{renderBasicFormInfo()}</Form>
              </Formik>
            </Grid>
          </Grid>
          <FormBuilderComponent
            formData={formDataFields.formFields}
            onFormSave={handleFormSave}
            handleDiscardModal={() => setDiscardModal(!discardModal)}
            isTemplate={formDataFields.isTemplate}
            saveRef={formBuilderSaveRef}
            onSchemaChange={(dirty) => setBuilderDirty(dirty)}
          />

          <CommonModal
            open={discardModal}
            message="Are you sure you want to discard your changes?"
            buttonProps={[
              {
                onClick: () => {
                  setDiscardModal(false);
                },
              },
              {
                label: 'Confirm',
                onClick: () => {
                  setform(formCopy);
                  setDiscardModal(false);
                  setBuilderDirty(false);
                  setFormDataFields({
                    ...formDataFields,
                    formFields: form['json_object'],
                    isTemplate: true,
                  });
                },
              },
            ]}
            hideHeader
            id="hideHeader"
          />
        </div>
      </div>
    )
  );
}
